import React, {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import useUser from '@/hooks/useUser';
import {useUserRoles} from '@/hooks/User/useUserRoles';
import usePlatform from '@/hooks/usePlatform';
import {
    filterStepsByMode,
    filterStepsByRole,
    filterStepsByTour,
    getNavTarget,
    getTutorialSteps,
    isPatternPath,
    isStepOnPage,
    resolveTourId,
    toRelativePath,
    type TutorialStep,
    type TutorialMode,
} from '@/lib/tutorial/tutorialSteps';

type TutorialContextValue = {
    isActive: boolean;
    mode: TutorialMode | null;
    currentStep: TutorialStep | null;
    currentStepIndex: number;
    totalSteps: number;
    isOnCorrectPage: boolean;
    isNearMiss: boolean;
    /** Start the full guided tour (across all pages). */
    startFull: () => void;
    /** Start a tour limited to steps relevant to the current page. */
    startPage: () => void;
    next: () => void;
    back: () => void;
    skip: () => void;
};

interface ReduxUser {
    authorities?: string[];
    allAuthorities?: string[];
}

interface TutorialAppState {
    init: {user: ReduxUser | null};
}

const TutorialContext = createContext<TutorialContextValue | undefined>(undefined);

export const TutorialProvider: React.FC<{children: React.ReactNode}> = ({children}) => {
    const {t} = useTranslation();
    const {pathname} = useLocation();
    const navigate = useNavigate();
    const platform = usePlatform();
    const user = useUser();
    const {activeRoles} = useUserRoles(user);

    // REMUS routes are prefixed with `/:platform`; match steps against the stripped path.
    const relativePath = toRelativePath(pathname, platform);

    const [mode, setMode] = useState<TutorialMode | null>(null);
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [pageTourId, setPageTourId] = useState<string | null>(null);
    const lastNavRef = useRef<string | null>(null);

    const allSteps = useMemo(
        () => filterStepsByRole(getTutorialSteps(t), activeRoles),
        [t, activeRoles]
    );

    const steps = useMemo(() => {
        if (mode === 'page') {
            return pageTourId ? filterStepsByTour(filterStepsByMode(allSteps, 'page'), pageTourId) : [];
        }
        if (mode === 'full') return filterStepsByMode(allSteps, 'full');
        return allSteps;
        // For page mode, snapshot the tour id at start so steps don't change as user navigates
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, allSteps, pageTourId]);

    const isActive = mode !== null;
    const currentStep = isActive ? (steps[currentStepIndex] ?? null) : null;

    const isOnCorrectPage = currentStep ? isStepOnPage(currentStep, relativePath) : false;
    const isNearMiss = false;

    useEffect(() => {
        setCurrentStepIndex((i) => Math.min(i, Math.max(steps.length - 1, 0)));
    }, [steps.length]);

    // Page-mode steps are snapshotted to their starting page; if the user navigates away,
    // page mode suppresses the wrong-page prompt and the tour would silently "vanish".
    // Auto-end it so the user can restart on the new page.
    useEffect(() => {
        if (mode !== 'page' || steps.length === 0) return;
        if (!steps.some((s) => isStepOnPage(s, relativePath))) {
            setMode(null);
            setCurrentStepIndex(0);
        }
    }, [mode, relativePath, steps]);

    // While a tour is active and the current step lives on another page, drive the router
    // to that page automatically. A ref guard prevents a navigate loop if the resolved path
    // and the step's page momentarily disagree.
    useEffect(() => {
        if (!isActive || !currentStep) return;
        if (isStepOnPage(currentStep, relativePath)) {
            lastNavRef.current = null;
            return;
        }
        // Entity pages (pattern paths) have no concrete URL to navigate to — their tours
        // start from the page itself, and leaving the page ends them (effect above).
        if (isPatternPath(currentStep.page)) return;
        const target = getNavTarget(t, currentStep, platform);
        const key = `${currentStep.id}@${target.path}`;
        if (lastNavRef.current === key) return; // already navigated for this step
        lastNavRef.current = key;
        navigate(target.path);
    }, [isActive, currentStep, relativePath, navigate, platform, t]);

    const startFull = useCallback(() => {
        setMode('full');
        setCurrentStepIndex(0);
    }, []);

    const startPage = useCallback(() => {
        const id = resolveTourId(allSteps, relativePath);
        setPageTourId(id);
        setMode('page');
        setCurrentStepIndex(0);
    }, [allSteps, relativePath]);

    const finish = useCallback(() => {
        setMode(null);
        setCurrentStepIndex(0);
        lastNavRef.current = null;
    }, []);

    const next = useCallback(() => {
        const nextIndex = currentStepIndex + 1;
        if (nextIndex >= steps.length) finish();
        else setCurrentStepIndex(nextIndex);
    }, [currentStepIndex, steps.length, finish]);

    const back = useCallback(() => {
        setCurrentStepIndex((i) => Math.max(0, i - 1));
    }, []);

    const skip = useCallback(() => {
        finish();
    }, [finish]);

    const value = useMemo<TutorialContextValue>(
        () => ({
            isActive,
            mode,
            currentStep,
            currentStepIndex,
            totalSteps: steps.length,
            isOnCorrectPage,
            isNearMiss,
            startFull,
            startPage,
            next,
            back,
            skip,
        }),
        [
            isActive,
            mode,
            currentStep,
            currentStepIndex,
            steps.length,
            isOnCorrectPage,
            isNearMiss,
            startFull,
            startPage,
            next,
            back,
            skip,
        ]
    );

    return <TutorialContext.Provider value={value}>{children}</TutorialContext.Provider>;
};

export const useTutorial = (): TutorialContextValue => {
    const ctx = useContext(TutorialContext);
    if (!ctx) throw new Error('useTutorial must be used within TutorialProvider');
    return ctx;
};
